import { Link, useParams } from "react-router-dom";
import { useRef, useState } from "react";
import {
    MessageSquareText,
    Phone,
    ArrowLeft,
    Wrench,
    Clock,
    Car,
    CheckCircle2,
    Share2,
} from "lucide-react";
import { SMS_HREF, TEL_HREF, PHONE_DISPLAY, BRAND } from "@/lib/site";
import useWorkJobs from "@/hooks/useWorkJobs";
import useSEO from "@/hooks/useSEO";
import { track } from "@/lib/track";
import Breadcrumb from "@/components/Breadcrumb";

// 1080×1080 IG-ready overlay generated entirely client-side (no backend).
async function generateShareImage(job) {
    const W = 1080;
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = W;
    const ctx = canvas.getContext("2d");

    // Use the real on-site photo if available — that's the most "social"-worthy shot.
    const src = (job.workPhotos && job.workPhotos[0]) || job.cover || job.image;
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((res, rej) => {
        img.onload = res;
        img.onerror = rej;
        img.src = src;
    });

    // Cover-fill the photo.
    const r = Math.max(W / img.width, W / img.height);
    const dw = img.width * r;
    const dh = img.height * r;
    ctx.drawImage(img, (W - dw) / 2, (W - dh) / 2, dw, dh);

    // Dark gradient bottom overlay
    const grad = ctx.createLinearGradient(0, W * 0.35, 0, W);
    grad.addColorStop(0, "rgba(0,0,0,0.0)");
    grad.addColorStop(0.55, "rgba(0,0,0,0.65)");
    grad.addColorStop(1, "rgba(0,0,0,0.95)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, W);

    // Top brand bar
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, 0, W, 110);
    ctx.fillStyle = "#dc2626";
    ctx.fillRect(0, 110, W, 6);
    ctx.fillStyle = "#ffffff";
    ctx.font = "900 42px ui-sans-serif, system-ui, -apple-system, Helvetica Neue, sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(BRAND.toUpperCase(), 56, 60);

    // Title (wrap to 2 lines max)
    ctx.fillStyle = "#ffffff";
    ctx.font = "900 72px ui-sans-serif, system-ui, -apple-system, Helvetica Neue, sans-serif";
    const title = job.title.toUpperCase();
    const lines = wrapText(ctx, title, W - 112);
    const titleY = W - 360;
    lines.slice(0, 2).forEach((line, i) => {
        ctx.fillText(line, 56, titleY + i * 78);
    });

    // Savings stat
    const savings = job.pricing ? Math.max(0, job.pricing.dealership - job.pricing.us) : 0;
    const savingsPct = job.pricing?.dealership ? Math.round((savings / job.pricing.dealership) * 100) : 0;
    ctx.fillStyle = "#dc2626";
    ctx.font = "900 56px ui-sans-serif, system-ui, sans-serif";
    if (job.pricing) ctx.fillText(`$${job.pricing.us}`, 56, W - 170);
    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "700 28px ui-sans-serif, system-ui, sans-serif";
    if (job.pricing) ctx.fillText(`vs $${job.pricing.dealership} dealer  ·  Save ${savingsPct}%`, 56, W - 120);

    // CTA
    ctx.fillStyle = "#ffffff";
    ctx.font = "900 32px ui-sans-serif, system-ui, sans-serif";
    ctx.fillText(`Text ${PHONE_DISPLAY}`, 56, W - 56);

    return canvas.toDataURL("image/png");
}

function wrapText(ctx, text, maxWidth) {
    const words = text.split(" ");
    const lines = [];
    let line = "";
    for (const w of words) {
        const test = line ? line + " " + w : w;
        if (ctx.measureText(test).width > maxWidth && line) {
            lines.push(line);
            line = w;
        } else {
            line = test;
        }
    }
    if (line) lines.push(line);
    return lines;
}

export default function WorkDetail() {
    const { slug } = useParams();
    const { jobs, loading, error } = useWorkJobs(slug);
    const job = jobs.find((g) => g.slug === slug);
    const [sharing, setSharing] = useState(false);
    const [shareUrl, setShareUrl] = useState(null);
    const linkRef = useRef(null);

    const handleShare = async () => {
        if (!job) return;
        setSharing(true);
        try {
            const dataUrl = await generateShareImage(job);
            setShareUrl(dataUrl);
            track("share_image_create", { job_slug: job.slug });
            // Auto-trigger download once generated
            setTimeout(() => linkRef.current?.click(), 0);
        } catch (e) {
            console.error("share image failed", e);
            alert("Couldn't build the share image — the photo may be blocked from copying. Try again or save the original photo manually.");
        } finally {
            setSharing(false);
        }
    };

    useSEO({
        title: job ? `${job.title} — Job Breakdown` : "Job Not Found",
        description: job?.caption,
        image: job?.image,
    });

    if (!job && loading) return <div role="status" data-testid="work-loading" className="lbp-container py-24 text-zinc-400">Loading job…</div>;
    if (!job && error) return <div role="alert" data-testid="work-load-error" className="lbp-container py-24 text-zinc-300">This job could not be loaded. Please try again shortly.</div>;
    if (!job) {
        return (
            <div className="lbp-container py-24 text-center">
                <h1 className="font-display font-black uppercase text-white text-3xl">
                    Job not found
                </h1>
                <Link
                    to="/gallery"
                    className="mt-6 inline-flex items-center gap-2 text-red-500 hover:underline"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to all work
                </Link>
            </div>
        );
    }

    const referenceOnly = !!job.referenceOnly;
    const hasPricing = job.pricing && Number.isFinite(job.pricing.us) && Number.isFinite(job.pricing.dealership);
    const savings = referenceOnly || !hasPricing ? 0 : Math.max(0, job.pricing.dealership - job.pricing.us);
    const savingsPct = referenceOnly || !hasPricing || !job.pricing.dealership
        ? 0
        : Math.round((savings / job.pricing.dealership) * 100);
    const heroImage = job.cover || job.image;
    const realPhotos = job.workPhotos || [];

    return (
        <div data-testid="work-detail-page">
            <Breadcrumb section={job.serviceType} />
            <section className="relative border-b border-white/10 bg-black">
                <div className="absolute inset-0 opacity-30">
                    <img src={heroImage} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
                </div>
                <div className="relative lbp-container py-12 sm:py-20">
                    <Link
                        to="/gallery"
                        data-testid="work-back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-400 hover:text-white"
                    >
                        <ArrowLeft className="w-4 h-4" /> See All Work
                    </Link>
                    <p className="mt-6 text-xs uppercase tracking-widest text-red-500 font-semibold">
                        {job.serviceType}
                    </p>
                    <h1 className="mt-2 break-words font-display font-black uppercase tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl">
                        {job.title}
                    </h1>
                    <p className="mt-4 max-w-2xl break-words text-zinc-300 text-base sm:text-lg leading-relaxed">
                        {job.caption}
                    </p>

                    <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-zinc-300">
                        {job.vehicle && <span className="inline-flex items-center gap-2" data-testid="work-vehicle">
                            <Car className="w-4 h-4 text-red-500" />
                            {job.vehicle}
                        </span>}
                        <span className="inline-flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-red-500" />
                            {job.serviceType}
                        </span>
                        {job.timeOnSite && <span className="inline-flex items-center gap-2" data-testid="work-time-on-site"><Clock className="w-4 h-4 text-red-500" />{job.timeOnSite}</span>}
                    </div>
                    {job.imported && <div className="mt-5 flex flex-wrap gap-4 text-xs text-zinc-400">
                        {job.postDate && <span data-testid="work-instagram-post-date">Posted on Instagram · {new Date(job.postDate).toLocaleDateString()}</span>}
                        {job.instagramUrl && <a href={job.instagramUrl} target="_blank" rel="noopener noreferrer" data-testid="work-instagram-source" className="text-red-400 hover:text-red-300 transition-colors">View original Instagram post</a>}
                    </div>}
                </div>
            </section>

            {realPhotos.length > 0 && (
                <section className="bg-black border-b border-white/10">
                    <div className="lbp-container py-14 sm:py-20">
                        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                            <div>
                                <p className="text-xs uppercase tracking-widest text-red-500 font-semibold">
                                    Photos from the job
                                </p>
                                <h2 className="mt-2 font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl lg:text-4xl">
                                    Real Work, Documented.
                                </h2>
                            </div>
                            <p className="text-xs sm:text-sm text-zinc-500 max-w-md">
                                {job.imported ? "Images and video thumbnails from the original Instagram post." : "Snapped on-site during the job. No stock photos here — this is exactly what got worked on."}
                            </p>
                        </div>
                        <div className={`grid gap-3 sm:gap-4 ${realPhotos.length === 1 ? "grid-cols-1 max-w-2xl" : realPhotos.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"}`}>
                            {realPhotos.map((src, i) => (
                                <div
                                    key={i}
                                    data-testid={`work-real-photo-${i}`}
                                    className="relative overflow-hidden rounded-sm border border-white/10 bg-zinc-900 aspect-[4/3]"
                                >
                                    <img
                                        src={src}
                                        alt={`${job.title} — photo ${i + 1}`}
                                        className="w-full h-full object-contain bg-zinc-950"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="bg-zinc-950 border-b border-white/10">
                <div className="lbp-container py-14 sm:py-20 grid lg:grid-cols-5 gap-10">
                    <div className="lg:col-span-3">
                        <h2 className="font-display font-black uppercase tracking-tight text-white text-2xl sm:text-3xl">
                            What Got Done
                        </h2>
                        <div className="mt-6 space-y-5 text-zinc-300 leading-relaxed">
                            {job.details.map((p, i) => (
                                <p key={i} className="whitespace-pre-wrap break-words" data-testid={`work-detail-paragraph-${i}`}>{p}</p>
                            ))}
                        </div>

                        {job.customerComplaint && <div className="mt-6" data-testid="work-customer-complaint"><h2 className="text-base font-bold text-white">Customer Complaint</h2><p className="mt-2 whitespace-pre-wrap break-words text-sm text-zinc-300">{job.customerComplaint}</p></div>}
                        {job.diagnosis && <div className="mt-6" data-testid="work-diagnosis"><h2 className="text-base font-bold text-white">Diagnosis</h2><p className="mt-2 whitespace-pre-wrap break-words text-sm text-zinc-300">{job.diagnosis}</p></div>}
                        {job.pricing?.includes && <div className="mt-8 rounded-sm border border-white/10 bg-zinc-900 p-6">
                            <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">
                                Includes
                            </div>
                            <p className="mt-2 text-zinc-300 text-sm leading-relaxed">
                                {job.pricing.includes}
                            </p>
                        </div>}
                    </div>

                    <div className="lg:col-span-2">
                        {referenceOnly ? (
                            <div
                                className="rounded-sm border border-zinc-700/60 bg-zinc-900 p-6 sm:p-7 sticky top-24"
                                data-testid="work-reference-card"
                            >
                                <div className="text-[11px] uppercase tracking-widest text-zinc-400 font-bold">
                                    For Reference Only
                                </div>
                                <h3 className="mt-1 font-display font-black uppercase tracking-tight text-white text-xl">
                                    Not a Service We Offer
                                </h3>
                                <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
                                    {job.referenceNote ||
                                        "Listed here to show experience — Limit Break does not currently offer this service."}
                                </p>
                                <p className="mt-4 text-[11px] text-zinc-500 leading-relaxed">
                                    Need a service we DO offer? Browse the gallery or text us — we'll point you to the right resource.
                                </p>
                                <div className="mt-6 grid gap-2">
                                    <Link
                                        to="/services"
                                        data-testid="work-detail-services-link"
                                        className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                    >
                                        See What We Do Offer
                                    </Link>
                                    <a
                                        href={TEL_HREF}
                                        data-testid="work-detail-call-button"
                                        className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white text-zinc-200 px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-xs"
                                    >
                                        <Phone className="w-4 h-4" />
                                        Call {PHONE_DISPLAY}
                                    </a>
                                </div>
                            </div>
                        ) : (
                        <div
                            className="rounded-sm border border-red-600/30 bg-gradient-to-br from-red-600/10 via-zinc-950 to-zinc-950 p-6 sm:p-7 sticky top-24"
                            data-testid={hasPricing ? "work-pricing-card" : "work-quote-card"}
                        >
                            <div className="text-[11px] uppercase tracking-widest text-red-500 font-bold">
                                {hasPricing ? "Honest Pricing" : "Your vehicle, your quote"}
                            </div>
                            <h3 className="mt-1 font-display font-black uppercase tracking-tight text-white text-2xl">
                                {hasPricing ? "Limit Break vs. Dealership" : "Need Similar Work?"}
                            </h3>

                            {hasPricing && <div className="mt-6 grid gap-3">
                                <div className="rounded-sm border border-white/10 bg-zinc-900 p-4">
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-xs uppercase tracking-widest text-zinc-400">
                                            Dealership
                                        </span>
                                        <span className="font-display font-bold text-zinc-400 text-2xl line-through decoration-red-600/70 decoration-[3px]">
                                            ${job.pricing.dealership}
                                        </span>
                                    </div>
                                </div>
                                <div className="rounded-sm border border-red-600/40 bg-red-600/15 p-4">
                                    <div className="flex items-baseline justify-between">
                                        <span className="text-xs uppercase tracking-widest text-red-400 font-bold">
                                            Limit Break
                                        </span>
                                        <span className="font-display font-black text-white text-3xl">
                                            ${job.pricing.us}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-center mt-1">
                                    <span className="inline-flex items-center gap-1 text-sm text-green-400 font-semibold">
                                        <CheckCircle2 className="w-4 h-4" />
                                        You save ${savings} ({savingsPct}% off dealership)
                                    </span>
                                </div>
                            </div>}

                            {hasPricing && <p className="mt-5 text-[11px] text-zinc-500 leading-relaxed">
                                * Quoted price represents typical labor for this type of job. Final price may vary based on your specific vehicle, parts chosen, and condition. Text for an exact quote.
                            </p>}

                            <div className="mt-6 grid gap-2">
                                <a
                                    href={SMS_HREF}
                                    data-testid="work-detail-text-button"
                                    className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-3.5 rounded-sm font-display font-black uppercase tracking-wide text-sm"
                                >
                                    <MessageSquareText className="w-4 h-4" />
                                    Text For My Quote
                                </a>
                                <a
                                    href={TEL_HREF}
                                    data-testid="work-detail-call-button"
                                    className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-5 py-3.5 rounded-sm font-display font-bold uppercase tracking-wide text-sm"
                                >
                                    <Phone className="w-4 h-4" />
                                    Call {PHONE_DISPLAY}
                                </a>
                                <button
                                    type="button"
                                    onClick={handleShare}
                                    disabled={sharing}
                                    data-testid="work-detail-share-button"
                                    className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-red-500/60 hover:text-red-400 text-zinc-300 px-5 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-xs disabled:opacity-50"
                                >
                                    <Share2 className="w-4 h-4" />
                                    {sharing ? "Building image…" : "Generate Share Image"}
                                </button>
                                {shareUrl && (
                                    <a
                                        ref={linkRef}
                                        href={shareUrl}
                                        download={`limit-break-${job.slug}.png`}
                                        data-testid="work-detail-share-download"
                                        className="text-[11px] text-center text-zinc-500 hover:text-white underline"
                                    >
                                        Re-download share image
                                    </a>
                                )}
                            </div>
                        </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
